import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Video, Wind, Activity, RefreshCw } from 'lucide-react';

const Live = () => {
  const { language } = useLanguage();
  const [timestamp, setTimestamp] = useState(Date.now());
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const widgetLoaded = useRef(false);

  // Force reload images by updating timestamp
  const forceReloadWindImages = () => {
    const now = Date.now();
    setTimestamp(now);
    setLastUpdate(new Date(now));
    console.log('✅ Wind images timestamp updated at:', new Date(now).toLocaleTimeString());
  };

  // Initial load
  useEffect(() => {
    forceReloadWindImages();
  }, []);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      forceReloadWindImages();
    }, 60000); // 60000ms = 1 minute

    return () => clearInterval(interval);
  }, []);

  // Load Windfinder Widget Script - Only once
  useEffect(() => {
    // Prevent double loading
    if (widgetLoaded.current) {
      return;
    }

    const widgetContainer = document.getElementById('windfinder-widget-container');
    if (!widgetContainer) {
      return;
    }

    // Check if script already exists
    const existingScript = document.getElementById('windfinder-widget-script');
    if (existingScript) {
      return;
    }

    // Mark as loaded
    widgetLoaded.current = true;

    const script = document.createElement('script');
    script.src = 'https://www.windfinder.com/widget/forecast/js/soma_bay_surf_motion?unit_wave=m&unit_rain=mm&unit_temperature=c&unit_wind=kts&unit_pressure=hPa&days=4&show_day=1&show_pressure=0&show_waves=0&show_rain=0';
    script.async = true;
    script.id = 'windfinder-widget-script';
    
    widgetContainer.appendChild(script);

    // No cleanup to prevent re-adding on re-renders
  }, []);

  return (
    <div className="min-h-screen" data-testid="live-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Activity className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">LIVE</h1>
          <p className="text-xl text-blue-100">
            {language === 'de' 
              ? 'Live-Kameras und aktuelle Windbedingungen' 
              : 'Live Cameras and Current Wind Conditions'}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {language === 'de' 
                ? 'Die Soma Bay am Roten Meer' 
                : 'Soma Bay at the Red Sea'}
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed mb-6">
              {language === 'de'
                ? 'Die Soma Bay am Roten Meer gehört zu den besten Flachwasserrevieren die wir kennen. Genussgleiten in türkisfarbenem Wasser – viel Platz – Windwellen.'
                : 'Soma Bay on the Red Sea is one of the best flat water areas we know. Gliding pleasure in turquoise water – lots of space – wind waves.'}
            </p>
            <p className="text-xl italic text-blue-600 mb-4">
              "The best surfer out there is the one having the most fun."
            </p>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              {language === 'de'
                ? 'Wellen die nicht höher sind als die Fusspads auf Eurem Board – lange Sandbänke, von denen Ihr praktisch barfuss auf´s Board springen könnt – richtig relaxt Surfen – total Entspannen und endlich alle Wunschmanöver lernen.'
                : 'Waves that are no higher than the foot pads on your board – long sandbanks from which you can practically jump barefoot onto the board – really relaxed surfing – total relaxation and finally learn all the maneuvers you want.'}
            </p>
          </div>
        </div>
      </section>

      {/* Live Cameras */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Video className="w-16 h-16 mx-auto mb-4 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'de' ? 'Live Kameras' : 'Live Cameras'}
            </h2>
            <p className="text-gray-600">
              {language === 'de' 
                ? 'Sehen Sie live, was an unserem Spot passiert' 
                : 'Watch live what\'s happening at our spot'}
            </p>
          </div>

          {/* Main Camera - Full Width */}
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-blue-600 text-white px-4 py-3">
                <h3 className="font-semibold text-center text-lg">Surfmotion Cam 1 - Hauptkamera</h3>
              </div>
              <div className="p-4">
                <div className="aspect-video bg-gray-100 rounded overflow-hidden">
                  <iframe 
                    src="https://www.cameraftp.com/Camera/Cameraplayer.aspx?parentID=389436799&shareID=17804862&isEmbedded=true&mode=5"
                    className="w-full h-full"
                    style={{ border: '0px', overflow: 'hidden' }}
                    scrolling="no"
                    title="Surfmotion Cam 1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Camera 3 - Full Width (same size as Cam 1) */}
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-blue-600 text-white px-4 py-3">
                <h3 className="font-semibold text-center text-lg">Surfmotion Cam 3</h3>
              </div>
              <div className="p-4">
                <div className="aspect-video bg-gray-100 rounded overflow-hidden">
                  <iframe 
                    src="https://www.cameraftp.com/Camera/Cameraplayer.aspx?parentID=390181829&shareID=17829642&isEmbedded=true&mode=5"
                    className="w-full h-full"
                    style={{ border: '0px', overflow: 'hidden' }}
                    scrolling="no"
                    title="Surfmotion Cam 3"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wind Data */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Wind className="w-16 h-16 mx-auto mb-4 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'de' ? 'Wind Daten' : 'Wind Data'}
            </h2>
            <p className="text-gray-600 mb-2">
              {language === 'de' 
                ? 'Aktuelle Windgeschwindigkeit und -richtung' 
                : 'Current wind speed and direction'}
            </p>
            <div className="flex items-center justify-center space-x-4">
              <p className="text-sm text-gray-500">
                {language === 'de' ? 'Automatische Aktualisierung: ' : 'Auto-refresh: '}
                {language === 'de' ? 'Jede Minute' : 'Every minute'}
                {' • '}
                {language === 'de' ? 'Zuletzt aktualisiert: ' : 'Last updated: '}
                {lastUpdate.toLocaleTimeString(language === 'de' ? 'de-DE' : 'en-US')}
              </p>
              <button
                onClick={forceReloadWindImages}
                className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                title={language === 'de' ? 'Jetzt aktualisieren' : 'Refresh now'}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                {language === 'de' ? 'Aktualisieren' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Wind Direction */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-3">
                <h3 className="font-semibold text-center">
                  {language === 'de' ? 'Windrichtung' : 'Wind Direction'}
                </h3>
              </div>
              <div className="p-4 bg-gray-50 flex items-center justify-center">
                <img 
                  src={`https://wind.surfmotion.com/wind/WindDirection.gif?t=${timestamp}`}
                  alt="Wind Direction"
                  className="max-w-full h-auto rounded"
                  key={`wind-direction-${timestamp}`}
                />
              </div>
            </div>

            {/* Wind Speed History */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-3">
                <h3 className="font-semibold text-center">
                  {language === 'de' ? 'Windgeschwindigkeit (kt)' : 'Wind Speed (kt)'}
                </h3>
              </div>
              <div className="p-4 bg-gray-50 flex items-center justify-center">
                <img 
                  src={`https://wind.surfmotion.com/wind/HiWindSpeedHistory.gif?t=${timestamp}`}
                  alt="Wind Speed History"
                  className="max-w-full h-auto rounded"
                  key={`wind-speed-history-${timestamp}`}
                />
              </div>
            </div>

            {/* Wind Speed */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-3">
                <h3 className="font-semibold text-center">
                  {language === 'de' ? 'Windgeschwindigkeit' : 'Wind Speed'}
                </h3>
              </div>
              <div className="p-4 bg-gray-50 flex items-center justify-center">
                <img 
                  src={`https://wind.surfmotion.com/wind/WindSpeed.gif?t=${timestamp}`}
                  alt="Wind Speed"
                  className="max-w-full h-auto rounded"
                  key={`wind-speed-${timestamp}`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Windfinder Widget */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'de' ? 'Windbericht von Windfinder' : 'Wind Forecast by Windfinder'}
            </h2>
            <p className="text-gray-600">
              {language === 'de' 
                ? 'Detaillierte Windvorhersage für die kommenden Tage' 
                : 'Detailed wind forecast for the coming days'}
            </p>
          </div>

          <div className="flex justify-center">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-6xl w-full">
              <div id="windfinder-widget-container" className="p-2 min-h-[350px]">
                {/* Windfinder widget will be loaded here via script */}
                <noscript>
                  <a rel="nofollow" href="https://www.windfinder.com/forecast/soma_bay_surf_motion?utm_source=forecast&utm_medium=web&utm_campaign=homepageweather&utm_content=noscript-forecast">
                    Wind forecast for Soma Bay / Surf Motion
                  </a> provided by <a rel="nofollow" href="https://www.windfinder.com?utm_source=forecast&utm_medium=web&utm_campaign=homepageweather&utm_content=noscript-logo">
                    windfinder.com
                  </a>
                </noscript>
              </div>
              <div className="text-center py-3 border-t border-gray-200 bg-gray-50">
                <a 
                  href="https://www.windfinder.com/forecast/soma_bay_surf_motion?utm_source=forecast&utm_medium=web&utm_campaign=homepageweather&utm_content=noscript-forecast"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline text-sm"
                >
                  {language === 'de' ? 'Vollständige Vorhersage auf Windfinder.com' : 'Full forecast on Windfinder.com'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WeatherLink Widget */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'de' ? 'Aktuelle Wetterdaten' : 'Current Weather Data'}
            </h2>
            <p className="text-gray-600">
              {language === 'de' 
                ? 'Live Wetterdaten direkt von unserer Wetterstation' 
                : 'Live weather data directly from our weather station'}
            </p>
          </div>

          <div className="flex justify-center">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <iframe 
                src="https://www.weatherlink.com/embeddablePage/show/b1ad7d2df9bd45149101a9c39d35ff76/wide"
                width="560"
                height="480"
                frameBorder="0"
                title="WeatherLink Weather Station"
                className="max-w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {language === 'de' 
              ? 'Perfekte Bedingungen heute?' 
              : 'Perfect conditions today?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {language === 'de'
              ? 'Komm vorbei und erlebe unvergessliche Sessions!'
              : 'Come by and experience unforgettable sessions!'}
          </p>
          <a
            href="https://reservations.surfmotion.com/reservation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            {language === 'de' ? 'Vorab-Registrierung' : 'Pre-Registration'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Live;
